/**
 * Component Factory - Base para Crear Componentes
 * =================================================
 *
 * Este módulo proporciona utilidades y clases base para crear
 * componentes AngularJS de manera consistente y rápida.
 *
 * USO:
 * ```typescript
 * import { createComponent, BaseController } from './_base/component-factory';
 *
 * const myComponent = createComponent({
 *   name: 'myComponent',
 *   moduleName: 'ec.myComponent',
 *   template: '<div>...</div>',
 *   bindings: { title: '@' },
 *   controller: MyController
 * });
 * ```
 */

import angular, { IModule, IScope, IAttributes, IAugmentedJQuery, IComponentOptions } from 'angular';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Configuración para crear un componente
 */
export interface ComponentConfig {
  /** Nombre del componente (camelCase) */
  name: string;
  /** Nombre del módulo Angular */
  moduleName: string;
  /** Template HTML */
  template: string;
  /** Bindings del componente */
  bindings?: Record<string, string>;
  /** Clase controlador */
  controller?: angular.IControllerConstructor;
  /** Dependencias del módulo */
  dependencies?: string[];
  /** Habilitar transclusion */
  transclude?: boolean;
}

/**
 * Resultado de crear un componente
 */
export interface ComponentResult {
  module: IModule;
  moduleName: string;
  componentName: string;
  definition: IComponentOptions;
}

/**
 * Configuración para crear stories
 */
export interface StoryConfigOptions {
  title: string;
  moduleName: string;
  argTypes?: Record<string, unknown>;
  parameters?: Record<string, unknown>;
}

/**
 * Opciones para el template de story
 */
export interface StoryTemplateOptions {
  wrapper?: string;
  wrapperStyle?: string;
}

// ============================================================================
// BASE CONTROLLER
// ============================================================================

/**
 * Clase base para controladores de componentes
 *
 * Proporciona funcionalidades comunes:
 * - Inyección de dependencias estándar
 * - Hooks de ciclo de vida
 * - Métodos utilitarios
 *
 * @example
 * class MyController extends BaseController {
 *   $onInit() {
 *     super.$onInit();
 *     // Tu código aquí
 *   }
 * }
 */
export class BaseController {
  static $inject = ['$scope', '$element', '$attrs'];

  protected $scope: IScope | null;
  protected $element: IAugmentedJQuery | null;
  protected $attrs: IAttributes | null;
  protected _initialized: boolean = false;

  constructor($scope: IScope, $element: IAugmentedJQuery, $attrs: IAttributes) {
    this.$scope = $scope;
    this.$element = $element;
    this.$attrs = $attrs;
  }

  /**
   * Hook de inicialización
   * Sobrescribir en clases hijas
   */
  $onInit(): void {
    this._initialized = true;
  }

  /**
   * Hook de cambios
   */
  $onChanges(_changes: Record<string, unknown>): void {
    // Sobrescribir en clases hijas si es necesario
  }

  /**
   * Hook de destrucción
   */
  $onDestroy(): void {
    this.$scope = null;
    this.$element = null;
    this.$attrs = null;
  }

  /**
   * Verificar si el componente está inicializado
   */
  isInitialized(): boolean {
    return this._initialized;
  }

  /**
   * Emitir evento hacia arriba en el scope
   */
  emit(eventName: string, data?: unknown): void {
    if (this.$scope) {
      this.$scope.$emit(eventName, data);
    }
  }

  /**
   * Broadcast evento hacia abajo en el scope
   */
  broadcast(eventName: string, data?: unknown): void {
    if (this.$scope) {
      this.$scope.$broadcast(eventName, data);
    }
  }

  /**
   * Escuchar evento en el scope
   */
  on(eventName: string, callback: (event: angular.IAngularEvent, ...args: unknown[]) => void): () => void {
    if (this.$scope) {
      return this.$scope.$on(eventName, callback);
    }
    return () => {};
  }

  /**
   * Ejecutar callback en el próximo digest cycle
   */
  nextTick(callback: () => void): void {
    if (this.$scope) {
      this.$scope.$evalAsync(callback);
    }
  }
}

// ============================================================================
// COMPONENT FACTORY
// ============================================================================

/**
 * Crear un componente Angular con configuración estándar
 */
export function createComponent(config: ComponentConfig): ComponentResult {
  const {
    name,
    moduleName,
    template,
    bindings = {},
    controller = BaseController,
    dependencies = ['ui.bootstrap'],
    transclude = false
  } = config;

  // Validar configuración requerida
  if (!name) throw new Error('Component name is required');
  if (!moduleName) throw new Error('Module name is required');
  if (!template) throw new Error('Template is required');

  // Definición del componente
  const componentDefinition: IComponentOptions = {
    template,
    controller,
    bindings,
    transclude
  };

  // Crear módulo Angular
  const module = angular
    .module(moduleName, dependencies)
    .component(name, componentDefinition);

  return {
    module,
    moduleName,
    componentName: name,
    definition: componentDefinition
  };
}

// ============================================================================
// STORY FACTORY
// ============================================================================

/**
 * Crear configuración base para stories de Storybook
 *
 * Note: This function should be used with the withAngular decorator imported separately
 * in the stories file due to circular dependency issues.
 */
export function createStoryConfig(
  config: StoryConfigOptions,
  withAngularFn: (modules: string[]) => unknown
): Record<string, unknown> {
  const {
    title,
    moduleName,
    argTypes = {},
    parameters = {}
  } = config;

  return {
    title,
    decorators: [withAngularFn([moduleName])],
    parameters: {
      layout: 'centered',
      ...parameters
    },
    argTypes
  };
}

/**
 * Crear template function para stories
 */
export function createStoryTemplate(
  componentTag: string,
  attributes: string[] = [],
  options: StoryTemplateOptions = {}
): (args: Record<string, unknown>) => string {
  const { wrapper = 'div', wrapperStyle = 'width: 320px;' } = options;

  return (args: Record<string, unknown>): string => {
    const attrs = attributes
      .filter(attr => args[attr] !== undefined && args[attr] !== '')
      .map(attr => {
        const kebabAttr = attr.replace(/([A-Z])/g, '-$1').toLowerCase();
        const value = args[attr];

        if (typeof value === 'boolean') {
          return value ? `${kebabAttr}="true"` : `${kebabAttr}="false"`;
        }
        if (typeof value === 'function') {
          return `${kebabAttr}="${attr}()"`;
        }
        return `${kebabAttr}="${value}"`;
      })
      .join('\n      ');

    return `
    <${wrapper} style="${wrapperStyle}">
      <${componentTag}
        ${attrs}>
      </${componentTag}>
    </${wrapper}>
    `;
  };
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  BaseController,
  createComponent,
  createStoryConfig,
  createStoryTemplate
};
